import InvitationShareNotice from "../InvitationShareNotice";
import { callInvitationWatermark } from "../../Utils/invitationWatermarkApi";
import moment from "moment";
import React, { useCallback, useEffect, useRef, useMemo } from "react";
import { useState } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { addDocumentData, auth, getDeliveryRecords, getDocument, getShareLinkViews, isDemoDocument, removeAutopilotPackage, saveAutopilotPlanEdit, setRsvpAccessState } from "../../firebase";
import MessageScheduleList from "../MessageScheduleList";
import InvitationLinkCopyButton from "../InvitationLinkCopyButton";
import ActiveAutopilotBanner from "../ActiveAutopilotBanner";
import { RsvpEventSummary } from "../Settings";
import "./Dashboard.css";
import { determinStatus, exportToSpreadsheet, isBankEmpty } from "../../Utils";
import { getAttendeePreferenceLabels, hasEnabledRsvpOptions, normalizeRsvpDetails, normalizeRsvpOptions } from "../../Utils/rsvpOptions";
import { getEventCountdown, getStoredEventDetails } from "../../Utils/eventDetails";
import {
  AUTOPILOT_PACKAGE_STATE_FIELDS,
  buildAutopilotResetPatch,
  buildFuturePlanEditPatch,
  buildPackageAssignmentPatch,
  formatJerusalemDateTimeLocal,
  parseJerusalemDateTimeLocal,
  resolveAutopilotAdminPackageSelection,
  retainAutopilotAdminPackageSelection,
} from "../../Utils/autopilotAdmin";
import { callEventAutopilot } from "../../Utils/eventAutopilotApi";
import { getRecordUsage, recordOverageMessage } from "../../Utils/recordLimit";
import { getFreeRsvpVisibility, visiblePhoneKeys, isVisiblePhone } from "../../Utils/freeRsvpVisibility";

const AUTOPILOT_MESSAGE_PURPOSES = Object.freeze(["invitation", "unansweredReminder", "confirmedReminder"]);

const formatDashboardEventDate = (value) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : "";
};

const extractUploadcareUUID = (url = "") => {
  const match = String(url || "").match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
  return match ? match[1] : "";
};

const getContactTableDetails = (contact, seatingTables = []) => {
  const table = seatingTables.find((item) => item.id === contact?.tableNum);
  return table
    ? { name: table.name || "-", number: table.number || "-" }
    : { name: "ללא שולחן", number: "-" };
};

const MessageBankIcon = ({ channel }) => (
  <span className={`dashboard-bank-card__icon dashboard-bank-card__icon--${channel}`} aria-hidden="true">
    {channel === "whatsapp" ? (
      <svg viewBox="0 0 24 24"><path d="M19.1 4.9A9.8 9.8 0 0 0 3.7 16.7L2.3 22l5.4-1.4A9.8 9.8 0 0 0 22 11.9a9.7 9.7 0 0 0-2.9-7Zm-7 15.2a8 8 0 0 1-4.1-1.1l-.3-.2-3.2.8.9-3.1-.2-.3a8 8 0 1 1 6.9 3.9Zm4.4-6c-.2-.1-1.4-.7-1.7-.8-.2-.1-.4-.1-.6.1-.2.3-.6.8-.8 1-.1.2-.3.2-.5.1-1.5-.7-2.5-1.3-3.5-3-.3-.5.3-.5.8-1.6.1-.2 0-.4 0-.5l-.8-1.9c-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.2.3-1 1-1 2.4s1 2.8 1.2 3c.1.2 2 3.1 4.9 4.3 1.8.8 2.5.8 3.4.7 1-.2 1.4-1.2 1.6-1.7.2-.5.2-1 .1-1.1-.2-.2-.4-.2-.7-.4Z" /></svg>
    ) : (
      <svg viewBox="0 0 24 24"><path d="M3 5h18v14H3V5Zm2 2v.4l7 5.1 7-5.1V7H5Zm14 10V9.9l-7 5.1-7-5.1V17h14Z" /></svg>
    )}
  </span>
);

const StatusCardActionIcons = () => (
  <span className="dashboard-status-card__action-icons" aria-hidden="true">
    <span className="dashboard-status-card__action-icon dashboard-status-card__action-icon--view">
      <svg viewBox="0 0 24 24">
        <path d="M2.2 12s3.6-6 9.8-6 9.8 6 9.8 6-3.6 6-9.8 6-9.8-6-9.8-6Zm9.8 3.6a3.6 3.6 0 1 0 0-7.2 3.6 3.6 0 0 0 0 7.2Zm0-2a1.6 1.6 0 1 1 0-3.2 1.6 1.6 0 0 1 0 3.2Z" />
      </svg>
    </span>
    <span className="dashboard-status-card__action-icon dashboard-status-card__action-icon--pdf">PDF</span>
    <span className="dashboard-status-card__action-icon dashboard-status-card__action-icon--excel">X</span>
  </span>
);

// Full label on desktop, short label on mobile (see .dashboard-share-card__full/__short).
// __mini is a one-word label used by design-soft.css on phones.
const ShareCopyLabel = ({ full }) => (
  <>
    <span className="dashboard-share-card__full">{full}</span>
    <span className="dashboard-share-card__short">העתקת קישור</span>
    <span className="dashboard-share-card__mini">העתקה</span>
  </>
);

const ShareLinkViews = ({ count = 0 }) => {
  const formatted = Number(count || 0).toLocaleString("he-IL");
  return (
    <span className="dashboard-share-card__views" title="צפיות בקישור" aria-label={`${formatted} צפיות בקישור`}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
      <span>{formatted}</span>
    </span>
  );
};

const exportGuestListToPdf = async ({ data, title, addAlertText, seatingTables = [], rsvpOptions }) => {
  const guests = (data || []).filter((guest) => guest.name);
  const showRsvpOptions = hasEnabledRsvpOptions(rsvpOptions);
  const rowsPerPage = showRsvpOptions ? 18 : 24;
  const guestPages = guests.length > 0
    ? Array.from({ length: Math.ceil(guests.length / rowsPerPage) }, (_, pageIndex) =>
        guests.slice(pageIndex * rowsPerPage, (pageIndex + 1) * rowsPerPage)
      )
    : [[]];
  const reportHost = document.createElement("div");
  Object.assign(reportHost.style, { position: "fixed", right: "-10000px", top: "0" });
  const pageNodes = guestPages.map((pageGuests, pageIndex) => {
    const page = document.createElement("div");
    page.dir = "rtl";
    Object.assign(page.style, {
      width: "1100px", minHeight: "1450px", padding: "64px 58px",
      background: "#fff", color: "#173b3a", fontFamily: "Arial, sans-serif",
    });

    const heading = document.createElement("h1");
    heading.textContent = title;
    Object.assign(heading.style, { margin: "0 0 10px", textAlign: "center" });
    page.appendChild(heading);
    const summary = document.createElement("div");
    summary.textContent = `סה״כ אנשי קשר: ${guests.length} | עמוד ${pageIndex + 1} מתוך ${guestPages.length}`;
    Object.assign(summary.style, { textAlign: "center", marginBottom: "24px", color: "#5d7473" });
    page.appendChild(summary);

    const table = document.createElement("table");
    Object.assign(table.style, { width: "100%", borderCollapse: "collapse", fontSize: "16px" });
    const header = table.insertRow();
    ["#", "שם", "טלפון", "מוזמנים", "סטטוס", "שם שולחן", "מספר שולחן", ...(showRsvpOptions ? ["אפשרויות אורחים"] : [])].forEach((label) => {
      const cell = document.createElement("th");
      cell.textContent = label;
      Object.assign(cell.style, { padding: "11px 8px", border: "1px solid #d9e4e3", background: "#173b3a", color: "#fff", textAlign: "right" });
      header.appendChild(cell);
    });
    pageGuests.forEach((guest, rowIndex) => {
      const absoluteIndex = pageIndex * rowsPerPage + rowIndex;
      const tableDetails = getContactTableDetails(guest, seatingTables);
      const row = table.insertRow();
      if (absoluteIndex % 2) row.style.background = "#f4f8f7";
      const details = normalizeRsvpDetails(guest.rsvpDetails, parseInt(guest.howMany, 10) || 1);
      const preferenceSummary = details.attendees
        .map((attendee, attendeeIndex) => `אורח ${attendeeIndex + 1}: ${getAttendeePreferenceLabels(attendee, rsvpOptions).join(", ") || "ללא"}`)
        .join(" | ");
      const transportationSummary = rsvpOptions.transportation.enabled
        ? !details.transportation.answered
          ? "הסעה: לא נבחרה תשובה"
          : details.transportation.needsRide
            ? `הסעה: כן (${details.transportation.seats} מקומות)`
            : "הסעה: לא"
        : "";
      const rsvpSummary = [preferenceSummary, transportationSummary].filter(Boolean).join(" | ");
      [absoluteIndex + 1, guest.name, guest.phoneNumber, guest.howMany || 1, determinStatus(guest.status) || "", tableDetails.name, tableDetails.number, ...(showRsvpOptions ? [rsvpSummary] : [])].forEach((value) => {
        const cell = row.insertCell();
        cell.textContent = value;
        Object.assign(cell.style, { padding: "10px 8px", border: "1px solid #d9e4e3", textAlign: "right" });
      });
    });
    page.appendChild(table);
    reportHost.appendChild(page);
    return page;
  });
  document.body.appendChild(reportHost);

  try {
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const margin = 12;
    const contentWidth = pdf.internal.pageSize.getWidth() - margin * 2;
    const contentHeight = pdf.internal.pageSize.getHeight() - margin * 2;
    for (let pageIndex = 0; pageIndex < pageNodes.length; pageIndex += 1) {
      const canvas = await html2canvas(pageNodes[pageIndex], { scale: 2, backgroundColor: "#ffffff" });
      const naturalHeight = (canvas.height * contentWidth) / canvas.width;
      const imageHeight = Math.min(naturalHeight, contentHeight);
      const imageWidth = naturalHeight > contentHeight
        ? (canvas.width * contentHeight) / canvas.height
        : contentWidth;
      const horizontalOffset = (pdf.internal.pageSize.getWidth() - imageWidth) / 2;
      if (pageIndex > 0) pdf.addPage();
      pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", horizontalOffset, margin, imageWidth, imageHeight);
    }
    pdf.save(`${title}.pdf`);
  } catch (error) {
    addAlertText({ mode: "danger", text: "לא הצלחנו ליצור את קובץ ה־PDF. נסו שוב." });
  } finally {
    reportHost.remove();
  }
};

const Dashboard = ({ addAlertText, docID, isReadOnly = false, onOpenAutopilot, onPackageAssignmentChanged }) => {
  const [ownerName, setOwnerName] = useState("");
  const [eventDetails, setEventDetails] = useState({ date: "", time: "" });
  const [countdownNow, setCountdownNow] = useState(() => Date.now());

  const [approved, setApproved] = useState(0);
  const [notComing, setNotComing] = useState(0);
  const [pending, setPending] = useState(0);
  const [total, setTotal] = useState(0);
  const [approvedList, setApprovedList] = useState([]);
  const [notComingList, setNotComingList] = useState([]);
  const [pendingList, setPendingList] = useState([]);
  const [smsBank, setSmsBank] = useState(0);
  const [whatsAppBank, setWhatsAppBank] = useState("0");
  const [contacts, setContacts] = useState([]);
  const [seatingTables, setSeatingTables] = useState([]);
  const [seatingNotifications, setSeatingNotifications] = useState([]);
  const [deliveryRecords, setDeliveryRecords] = useState({ sms: [], wa: [], skipped: [], messageResults: [] });
  const [modalTitle, setModalTitle] = useState({ title: "", mode: "", icon: "", list: [] });
  const [smsSchdule, setSmsSchdule] = useState([]);
  const [watermarkState,setWatermarkState] = useState(null);
  const [watermarkBusy,setWatermarkBusy] = useState(false);
  const [watermarkShareNotice,setWatermarkShareNotice] = useState(false);
  const [watermarkShareExplanation,setWatermarkShareExplanation] = useState(false);
  const watermarkPendingRef = useRef(false);
  const [adminUnlocked, setAdminUnlocked] = useState(false);
  const [showPinPopup, setShowPinPopup] = useState(false);
  const [pinValue, setPinValue] = useState("");
  const [confirmDeleteIdx, setConfirmDeleteIdx] = useState(null);
  const keySequenceRef = useRef([]);
  const exportInProgressRef = useRef(false);
  const [exportInProgress, setExportInProgress] = useState("");
  const [invitationLinkVersion, setInvitationLinkVersion] = useState("");
  // Total opens of the two manual share links (one small read per Dashboard load).
  const [shareLinkViews, setShareLinkViews] = useState({ rsvp: 0, invite: 0 });
  useEffect(() => {
    if (!docID) return undefined;
    let active = true;
    getShareLinkViews({ docID }).then((views) => { if (active) setShareLinkViews(views); });
    return () => { active = false; };
  }, [docID]);
  const [invitationImageUrl, setInvitationImageUrl] = useState("");
  const [rsvpOptions, setRsvpOptions] = useState(() => normalizeRsvpOptions());
  const [eventAdminData, setEventAdminData] = useState({});
  const [autopilotTemplates, setAutopilotTemplates] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [recordLimitInput, setRecordLimitInput] = useState("");
  const [autopilotLifecycle, setAutopilotLifecycle] = useState({ status: "inactive" });
  const [autopilotPlan, setAutopilotPlan] = useState(null);
  const [autopilotDraftSteps, setAutopilotDraftSteps] = useState([]);
  const [autopilotAdminBusy, setAutopilotAdminBusy] = useState(false);
  const [showAutopilotResetConfirm, setShowAutopilotResetConfirm] = useState(false);
  const recordUsage = getRecordUsage(eventAdminData, contacts);
  const freeRsvpVisibility = useMemo(() => getFreeRsvpVisibility(eventAdminData, contacts), [eventAdminData, contacts]);
  const freeVisibilityLimited = freeRsvpVisibility.isLimited;
  const freeLimitReached = freeRsvpVisibility.isFree && freeRsvpVisibility.contactCount >= 50;
  const visibleContacts = freeRsvpVisibility.visibleContacts;
  const visiblePhones = visiblePhoneKeys(visibleContacts);

  const runExport = async (type, exportTask) => {
    if (exportInProgressRef.current) return;
    exportInProgressRef.current = true;
    setExportInProgress(type);
    const startedAt = Date.now();
    try {
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      await exportTask();
    } catch (error) {
      addAlertText({ mode: "danger", text: "לא הצלחנו ליצור את הקובץ. נסו שוב." });
    } finally {
      const remainingDisplayTime = Math.max(0, 350 - (Date.now() - startedAt));
      if (remainingDisplayTime > 0) {
        await new Promise((resolve) => setTimeout(resolve, remainingDisplayTime));
      }
      exportInProgressRef.current = false;
      setExportInProgress("");
    }
  };

  const downloadExcel = (data, title) =>
    runExport("excel", () => exportToSpreadsheet(data, title, seatingTables, rsvpOptions));

  const downloadPdf = (data, title) =>
    runExport("pdf", () => exportGuestListToPdf({ data, title, addAlertText, seatingTables, rsvpOptions }));

  useEffect(() => {
    const fireAsync = async () => {
      let doc = await getDocument({ docID: docID });

      const data = doc.data();
      setEventAdminData(data);
      setAutopilotLifecycle(data.autopilotLifecycle || { status: "inactive" });
      setAutopilotPlan(data.autopilotPlan || null);
      setSmsBank(data.smsBank);
      setWhatsAppBank(data.whatsAppBank !== undefined && data.whatsAppBank !== null ? data.whatsAppBank : "0");
      if (data.hasOwnProperty("contacts")) {
        setContacts(data.contacts);
      }
      setSeatingTables(Array.isArray(data.seatingTables) ? data.seatingTables : []);
      setRsvpOptions(normalizeRsvpOptions(data.invitationData?.additionalRsvpOptions));
      if (data.hasOwnProperty("messages")) {
        setSmsSchdule(data.messages ?? []);
      }
      setSeatingNotifications(Array.isArray(data.seatingNotifications) ? data.seatingNotifications : []);
      const loadedDeliveryRecords = await getDeliveryRecords({ docID });
      setDeliveryRecords(loadedDeliveryRecords);
      if (data.hasOwnProperty("contacts")) {
      }

      setOwnerName(data.title);
      setEventDetails(getStoredEventDetails(data));
      setInvitationLinkVersion(
        data.invitationLinkVersion || extractUploadcareUUID(data.invitationData?.attachmentData?.url)
      );
      setInvitationImageUrl(data.invitationData?.attachmentData?.url || data.imageUrl || "");
      return doc;
    };
    if (docID) {
      fireAsync();
    }
  }, [docID]);

  useEffect(() => {
    if (!eventDetails.date || !eventDetails.time) return undefined;
    setCountdownNow(Date.now());
    const intervalId = window.setInterval(() => setCountdownNow(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [eventDetails.date, eventDetails.time]);

  useEffect(() => {
    const pending = visibleContacts.filter(contact => !["approved", "notComing"].includes(contact.status));
    const approved = visibleContacts.filter(contact => contact.status === "approved");
    const notComing = visibleContacts.filter(contact => contact.status === "notComing");
    setPending(pending.length); setApproved(approved.length); setNotComing(notComing.length);
    setTotal(approved.reduce((sum, contact) => sum + (parseInt(contact.howMany, 10) || 1), 0));
    setPendingList(pending); setApprovedList(approved); setNotComingList(notComing);
    setModalTitle(current => ({ ...current, list: (current.list || []).filter(contact => visibleContacts.includes(contact)) }));
  }, [visibleContacts]);
  useEffect(() => {
    const SEQUENCE = ["5", "6", "7"];
    const handleKeyDown = (e) => {
      if (!e.ctrlKey) return;
      const key = e.key;
      if (!SEQUENCE.includes(key)) return;
      e.preventDefault();
      const seq = keySequenceRef.current;
      const expected = SEQUENCE[seq.length];
      if (key === expected) {
        seq.push(key);
        if (seq.length === SEQUENCE.length) {
          keySequenceRef.current = [];
          setShowPinPopup(true);
          setPinValue("");
        }
      } else {
        keySequenceRef.current = key === SEQUENCE[0] ? [key] : [];
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const loadWatermarkControl = async () => {
    setWatermarkBusy(true);
    try { const idToken=await auth.currentUser?.getIdToken(); if(!idToken)throw Error("AUTH_REQUIRED");
      setWatermarkState(await callInvitationWatermark({idToken,eventId:docID,action:"inspect"}));
    } catch(_error) { setWatermarkState(null); }
    finally {setWatermarkBusy(false);}
  };
  const handleWatermarkToggle = async () => {
    if(isReadOnly || !watermarkState || watermarkPendingRef.current)return;
    watermarkPendingRef.current=true;setWatermarkBusy(true);
    try {const idToken=await auth.currentUser?.getIdToken();
      const result=await callInvitationWatermark({idToken,eventId:docID,action:"toggle",enabled:!watermarkState.enabled});
      setWatermarkState(result);setInvitationImageUrl(result.imageUrl || "");
      setInvitationLinkVersion(result.invitationLinkVersion || "");
      if(result.changed){setWatermarkShareExplanation(false);setWatermarkShareNotice(true);}
      addAlertText({mode:"success",text:result.cleanEntitled ? "ההגדרה נשמרה. זכאות לתמונה נקייה קודמת לסימן המים." : "הגדרת סימן המים נשמרה."});
    }catch(_error){addAlertText({mode:"danger",text:"לא הצלחנו לשמור את הגדרת סימן המים."});}
    finally{watermarkPendingRef.current=false;setWatermarkBusy(false);}
  };
  useEffect(()=>{
    if(!adminUnlocked || !watermarkState?.preparing)return undefined;
    let active=true,timer;
    const poll=async()=>{
      try{const idToken=await auth.currentUser?.getIdToken();const result=await callInvitationWatermark({idToken,eventId:docID,action:"inspect"});
        if(!active || watermarkPendingRef.current)return;
        setWatermarkState(result);setInvitationImageUrl(result.imageUrl || "");
        if(result.invitationLinkVersion && result.invitationLinkVersion !== invitationLinkVersion){setInvitationLinkVersion(result.invitationLinkVersion);if(!result.preparing){setWatermarkShareExplanation(false);setWatermarkShareNotice(true);}}
      }catch(_error){}
      finally{if(active)timer=setTimeout(poll,1500);}
    };timer=setTimeout(poll,1500);return()=>{active=false;clearTimeout(timer);};
  },[adminUnlocked,docID,watermarkState?.preparing,invitationLinkVersion]);
  const loadAutopilotAdmin = async () => {
    const idToken = await auth.currentUser?.getIdToken();
    if (!idToken) throw new Error("AUTOPILOT_AUTH_REQUIRED");
    const result = await callEventAutopilot({ idToken, eventId: docID, action: "inspect" });
    setAutopilotTemplates(result.templates || []);
    setSelectedPackageId(resolveAutopilotAdminPackageSelection(result));
    setRecordLimitInput(result.recordLimit || "");
    setAutopilotLifecycle(result.lifecycle || { status: "inactive" });
    setAutopilotPlan(result.plan || null);
    setAutopilotDraftSteps(result.plan?.steps ? JSON.parse(JSON.stringify(result.plan.steps)) : []);
    setEventAdminData((current) => ({
      ...current,
      autopilotAssignment: result.assignment,
      rsvpAccess: result.rsvpAccess,
      recordLimit: result.recordLimit,
      autopilotLifecycle: result.lifecycle,
      autopilotPlan: result.plan,
    }));
  };

  const refreshAutopilotTemplates = useCallback(async () => {
    const idToken = await auth.currentUser?.getIdToken();
    if (!idToken) throw new Error("AUTOPILOT_AUTH_REQUIRED");
    const result = await callEventAutopilot({ idToken, eventId: docID, action: "listPackages" });
    const templates = result.templates || [];
    setAutopilotTemplates(templates);
    setSelectedPackageId((current) => retainAutopilotAdminPackageSelection({ current, templates }));
  }, [docID]);

  useEffect(() => {
    if (!adminUnlocked || !docID) return undefined;
    const refreshWhenCurrent = () => {
      if (document.visibilityState === "visible") {
        refreshAutopilotTemplates().catch(() => {});
      }
    };
    window.addEventListener("focus", refreshWhenCurrent);
    document.addEventListener("visibilitychange", refreshWhenCurrent);
    return () => {
      window.removeEventListener("focus", refreshWhenCurrent);
      document.removeEventListener("visibilitychange", refreshWhenCurrent);
    };
  }, [adminUnlocked, docID, refreshAutopilotTemplates]);

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    if (pinValue === "cracky") {
      setAdminUnlocked(true);
      loadWatermarkControl();
      setShowPinPopup(false);
      setAutopilotAdminBusy(true);
      try {
        await loadAutopilotAdmin();
      } catch (_error) {
        addAlertText({ mode: "warning", text: "מסך המנהל נפתח, אך נתוני החבילות לא נטענו." });
      } finally {
        setAutopilotAdminBusy(false);
      }
    } else {
      setShowPinPopup(false);
    }
    setPinValue("");
  };

  const coverDeficitsForChannel = (channel, newBankValue, currentSchedule, currentContacts) => {
    let bank = parseInt(newBankValue) || 0;
    const updatedSchedule = currentSchedule.map((m) => {
      if (m.status === "cancelled" || m.channel !== channel) return m;
      if (!moment(m.timeToSend).isAfter(moment())) return m;
      const count = currentContacts.filter(c => m.toGroup === "all" || c.status === m.toGroup).length;
      const deficit = Math.max(0, count - (m.recipientCount || 0));
      if (deficit <= 0 || bank <= 0) return m;
      const covered = Math.min(deficit, bank);
      bank -= covered;
      return { ...m, recipientCount: (m.recipientCount || 0) + covered };
    });
    return { updatedSchedule, remainingBank: bank };
  };

  const handleSaveSmsBank = async () => {
    if (isReadOnly) return;
    const newBank = parseInt(smsBank) || 0;
    const { updatedSchedule, remainingBank } = coverDeficitsForChannel("sms", newBank, smsSchdule, contacts);
    const scheduleChanged = JSON.stringify(updatedSchedule) !== JSON.stringify(smsSchdule);
    if (scheduleChanged) {
      setSmsSchdule(updatedSchedule);
      await addDocumentData({ docID, payload: { messages: updatedSchedule, smsBank: remainingBank, messageBankPurchased: true, messageGiftAdminReset: false } });
      setSmsBank(remainingBank);
    } else {
      await addDocumentData({ docID, payload: { smsBank: newBank, ...(newBank > 0 ? { messageBankPurchased: true, messageGiftAdminReset: false } : {}) } });
    }
    addAlertText({ mode: "success", text: "בנק SMS עודכן בהצלחה!" });
  };

  const handleSaveWhatsAppBank = async () => {
    if (isReadOnly) return;
    const newBank = parseInt(whatsAppBank) || 0;
    const { updatedSchedule, remainingBank } = coverDeficitsForChannel("whatsapp", newBank, smsSchdule, contacts);
    const scheduleChanged = JSON.stringify(updatedSchedule) !== JSON.stringify(smsSchdule);
    if (scheduleChanged) {
      setSmsSchdule(updatedSchedule);
      await addDocumentData({ docID, payload: { messages: updatedSchedule, whatsAppBank: remainingBank, messageBankPurchased: true, messageGiftAdminReset: false } });
      setWhatsAppBank(remainingBank);
    } else {
      await addDocumentData({ docID, payload: { whatsAppBank: newBank, ...(newBank > 0 ? { messageBankPurchased: true, messageGiftAdminReset: false } : {}) } });
    }
    addAlertText({ mode: "success", text: "בנק וואטסאפ עודכן בהצלחה!" });
  };

  const handleSavePackageAssignment = async () => {
    if (isReadOnly) return;
    if (["__free__", "__manual__"].includes(selectedPackageId)) {
      const classification = selectedPackageId === "__free__" ? "FREE" : "MANUAL";
      setAutopilotAdminBusy(true);
      try {
        const result = await setRsvpAccessState({
          docID,
          currentData: eventAdminData,
          classification,
          actor: { uid: auth.currentUser?.uid, email: auth.currentUser?.email },
        });
        setEventAdminData((current) => {
          const next = { ...current, rsvpAccess: result.rsvpAccess };
          AUTOPILOT_PACKAGE_STATE_FIELDS.forEach((field) => delete next[field]);
          return next;
        });
        setRecordLimitInput("");
        setAutopilotLifecycle({ status: "inactive" });
        setAutopilotPlan(null);
        setAutopilotDraftSteps([]);
        onPackageAssignmentChanged?.(null, null, result.rsvpAccess);
        addAlertText({ mode: "success", text: classification === "FREE" ? "מצב הגישה FREE נשמר." : "מצב הגישה MANUAL נשמר." });
      } catch (_error) {
        addAlertText({ mode: "danger", text: "שמירת מצב הגישה נכשלה." });
      } finally {
        setAutopilotAdminBusy(false);
      }
      return;
    }
    if (selectedPackageId === "__remove__") {
      setAutopilotAdminBusy(true);
      try {
        await removeAutopilotPackage({ docID });
        setEventAdminData((current) => {
          const next = { ...current };
          AUTOPILOT_PACKAGE_STATE_FIELDS.forEach((field) => delete next[field]);
          delete next.rsvpAccess;
          return next;
        });
        setRecordLimitInput("");
        setAutopilotLifecycle({ status: "inactive" });
        setAutopilotPlan(null);
        setAutopilotDraftSteps([]);
        setSelectedPackageId(autopilotTemplates[0]?.id || "");
        onPackageAssignmentChanged?.(null, null, null);
        addAlertText({ mode: "success", text: "חבילת הטייס האוטומטי והמצב המשויך אליה הוסרו." });
      } catch (_error) {
        addAlertText({ mode: "danger", text: "הסרת החבילה נכשלה." });
      } finally {
        setAutopilotAdminBusy(false);
      }
      return;
    }
    const template = autopilotTemplates.find((item) => item.id === selectedPackageId);
    try {
      const patch = buildPackageAssignmentPatch({
        currentData: eventAdminData,
        template,
        recordLimit: recordLimitInput,
        actor: { uid: auth.currentUser?.uid, email: auth.currentUser?.email },
      });
      await addDocumentData({ docID, payload: patch });
      setEventAdminData((current) => ({ ...current, ...patch }));
      onPackageAssignmentChanged?.(patch.autopilotAssignment, patch.recordLimit, patch.rsvpAccess);
      setAutopilotLifecycle(patch.autopilotLifecycle || autopilotLifecycle);
      if (Object.prototype.hasOwnProperty.call(patch, "autopilotPlan")) {
        setAutopilotPlan(patch.autopilotPlan);
        setAutopilotDraftSteps([]);
      }
      addAlertText({ mode: "success", text: "החבילה ומכסת הרשומות נשמרו. הטייס האוטומטי עדיין לא הופעל." });
    } catch (error) {
      addAlertText({ mode: "danger", text: error.message === "RECORD_LIMIT_INVALID" ? "יש להזין מכסת רשומות תקינה." : "שמירת החבילה נכשלה." });
    }
  };

  const updateAutopilotDraftStep = (id, patch) => {
    setAutopilotDraftSteps((steps) => steps.map((step) => step.id === id ? { ...step, ...patch } : step));
  };

  const handleAddAutopilotStep = () => {
    const scheduledAt = new Date(Date.now() + 86400000).toISOString();
    setAutopilotDraftSteps((steps) => [...steps, {
      id: `admin-${Date.now()}`,
      kind: "message",
      channel: "whatsapp",
      messagePurpose: "invitation",
      scheduledAt,
      executionStatus: "pending",
      timeZone: "Asia/Jerusalem",
      adjustments: ["ADMIN_ADDED"],
    }]);
  };

  const handleSaveFuturePlan = async () => {
    if (isReadOnly || !autopilotPlan) return;
    try {
      const localPatch = buildFuturePlanEditPatch({
        currentPlan: autopilotPlan,
        lifecycle: autopilotLifecycle,
        draftSteps: autopilotDraftSteps,
        allowStructuralEdits: true,
      });
      const saved = await saveAutopilotPlanEdit({ docID, editSource: "ctrl567", buildPatch: (latest) => buildFuturePlanEditPatch({ ...latest, draftSteps: autopilotDraftSteps, allowStructuralEdits: true }) });
      const patch = saved?.patch || localPatch;
      setAutopilotPlan(patch.autopilotPlan);
      setAutopilotDraftSteps(JSON.parse(JSON.stringify(patch.autopilotPlan.steps)));
      setAutopilotLifecycle(patch.autopilotLifecycle);
      setEventAdminData((current) => ({ ...current, ...patch }));
      addAlertText({ mode: "success", text: "תוכנית הטייס האוטומטי עודכנה." });
    } catch (error) {
      addAlertText({ mode: "danger", text: error.message === "COMPLETED_STEP_IMMUTABLE" ? "לא ניתן לשנות שלב שהושלם."
        : error.message === "STARTED_STEP_IMMUTABLE" ? "לא ניתן לשנות שלב שכבר התחיל להישלח. יש לרענן ולנסות שוב." : "יש לתקן את מועדי השלבים העתידיים." });
    }
  };

  const handleResetAutopilot = async () => {
    if (isReadOnly || autopilotAdminBusy) return;
    setAutopilotAdminBusy(true);
    try {
      const patch = buildAutopilotResetPatch({
        currentData: { ...eventAdminData, autopilotLifecycle, autopilotPlan },
        actor: { uid: auth.currentUser?.uid, email: auth.currentUser?.email },
      });
      await addDocumentData({ docID, payload: patch });
      setAutopilotLifecycle(patch.autopilotLifecycle);
      setAutopilotPlan(null);
      setAutopilotDraftSteps([]);
      setEventAdminData((current) => ({ ...current, ...patch }));
      setShowAutopilotResetConfirm(false);
      addAlertText({ mode: "success", text: "הטייס האוטומטי אופס. היסטוריית השליחות והחיוגים נשמרה." });
    } catch (_error) {
      addAlertText({ mode: "danger", text: "איפוס הטייס האוטומטי נכשל." });
    } finally {
      setAutopilotAdminBusy(false);
    }
  };

  const handleResetMessageGift = async () => {
    if (isReadOnly) return;
    const confirmed = window.confirm(
      "לאפשר ללקוח לקבל שוב את מתנת 10 הודעות הוואטסאפ ו־10 הודעות ה־SMS? המתנה תופיע רק כאשר שני הבנקים עומדים על 0."
    );
    if (!confirmed) return;

    await addDocumentData({
      docID,
      payload: {
        messageGiftClaimed: false,
        messageGiftClaimedAt: null,
        messageGiftAdminReset: true,
        messageGiftLastResetAt: new Date().toISOString(),
      },
    });
    addAlertText({
      mode: "success",
      text: "המתנה אופסה. היא תופיע ללקוח כאשר יתרות ה־SMS והוואטסאפ יהיו 0.",
    });
  };

  const handleDeleteMessage = async (index) => {
    if (isReadOnly) return;
    const msg = smsSchdule[index];
    const isPast = moment(msg.timeToSend).isBefore(moment());
    if (isPast) return;
    setConfirmDeleteIdx(index);
  };

  const confirmDelete = async () => {
    if (isReadOnly) return;
    const index = confirmDeleteIdx;
    setConfirmDeleteIdx(null);
    const msg = smsSchdule[index];
    const updated = smsSchdule.map((m, i) =>
      i === index ? { ...m, status: "cancelled" } : m
    );
    await addDocumentData({ docID, payload: { messages: updated } });
    setSmsSchdule(updated);
    const refund = msg.recipientCount || 0;
    if (refund > 0) {
      if (msg.channel === "sms") {
        const newBank = parseInt(String(smsBank), 10) + refund;
        await addDocumentData({ docID, payload: { smsBank: newBank } });
        setSmsBank(newBank);
      } else {
        const newBank = parseInt(String(whatsAppBank), 10) + refund;
        await addDocumentData({ docID, payload: { whatsAppBank: newBank } });
        setWhatsAppBank(newBank);
      }
    }
  };

  const unreadSeatingNotifications = seatingNotifications.filter((notification) => !notification.read && (!freeRsvpVisibility.isFree || isVisiblePhone(visiblePhones, notification.phoneNumber)));

  const dismissSeatingNotification = async (notificationId) => {
    if (isReadOnly) return;
    const updated = seatingNotifications.map((notification) =>
      notification.id === notificationId ? { ...notification, read: true } : notification
    );
    setSeatingNotifications(updated);
    await addDocumentData({ docID, payload: { seatingNotifications: updated } });
  };

  const getSkippedCountForMessage = (message) =>
    (deliveryRecords.skipped || []).filter((record) =>
      record.channel === (message.channel || "sms") &&
      record.timeToSend === message.timeToSend &&
      (!record.toGroup || record.toGroup === message.toGroup)
    ).length;

  const pendingCreditShortages = (() => {
    let smsBalance = Math.max(0, parseInt(String(smsBank || "0"), 10) || 0);
    let whatsAppBalance = Math.max(0, parseInt(String(whatsAppBank || "0"), 10) || 0);
    const shortages = new Map();

    smsSchdule
      .map((message, index) => ({ message, index }))
      .filter(({ message }) =>
        message.status !== "cancelled" &&
        message.status !== "error" &&
        moment(message.timeToSend).isAfter(moment())
      )
      .sort((a, b) => {
        const timeDifference = moment(a.message.timeToSend).valueOf() - moment(b.message.timeToSend).valueOf();
        return timeDifference || a.index - b.index;
      })
      .forEach(({ message }) => {
        const currentCount = contacts.filter((contact) =>
          message.toGroup === "all" || contact.status === message.toGroup
        ).length;
        const reserved = Math.max(0, parseInt(String(message.recipientCount || "0"), 10) || 0);
        const extraNeeded = Math.max(0, currentCount - reserved);
        const isWhatsApp = message.channel === "whatsapp";
        const availableBalance = isWhatsApp ? whatsAppBalance : smsBalance;
        const extraCovered = Math.min(extraNeeded, availableBalance);

        shortages.set(message, Math.max(0, extraNeeded - extraCovered));
        if (isWhatsApp) {
          whatsAppBalance -= extraCovered;
        } else {
          smsBalance -= extraCovered;
        }
      });

    return shortages;
  })();

  const getMissingCountForMessage = (message) => {
    if (message.status === "error") {
      return 0;
    }
    if (moment(message.timeToSend).isBefore(moment())) {
      return getSkippedCountForMessage(message);
    }
    return pendingCreditShortages.get(message) || 0;
  };

  const showSmsBank = !isBankEmpty(smsBank);
  const showWhatsAppBank = !isBankEmpty(whatsAppBank);
  const showAnyBank = showSmsBank || showWhatsAppBank;
  const eventDateLabel = formatDashboardEventDate(eventDetails.date);
  const eventCountdown = getEventCountdown(eventDetails, new Date(countdownNow));
  // Presentational only: drives the RSVP ring (design-soft.css).
  const rsvpCounted = approved + pending + notComing;
  const rsvpRingStyle = {
    "--ring-approved": rsvpCounted ? `${(approved / rsvpCounted) * 100}%` : "0%",
    "--ring-pending": rsvpCounted ? `${((approved + pending) / rsvpCounted) * 100}%` : "0%",
  };
  // SVG donut segments (pathLength 100) with a small gap between non-empty ones.
  const ringParts = [
    { key: "approved", value: approved },
    { key: "pending", value: pending },
    { key: "notComing", value: notComing },
  ];
  const ringGap = ringParts.filter((part) => part.value > 0).length > 1 ? 5 : 0;
  let ringStart = 0;
  const ringSegments = ringParts.map((part) => {
    const share = rsvpCounted ? (part.value / rsvpCounted) * 100 : 0;
    const length = Math.max(share - ringGap, share > 0 ? 0.01 : 0);
    const segment = { ...part, length, offset: -(ringStart + ringGap / 2) };
    ringStart += share;
    return segment;
  });
  return (
    <div className="dashboard-page col-12">
      {exportInProgress && (
        <div className="dashboard-export-overlay" role="status" aria-live="assertive" aria-busy="true">
          <div className="dashboard-export-overlay__dialog" dir="rtl">
            <span className="dashboard-export-overlay__spinner" aria-hidden="true" />
            <strong>
              {exportInProgress === "pdf" ? "מכין את קובץ ה־PDF…" : "מכין את קובץ האקסל…"}
            </strong>
            <span>התהליך עשוי להימשך מספר שניות</span>
          </div>
        </div>
      )}
      {showPinPopup && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.3)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <form onSubmit={handlePinSubmit} style={{ background: "#fff", padding: "24px", borderRadius: "8px", boxShadow: "0 4px 24px rgba(0,0,0,0.2)", display: "flex", flexDirection: "column", gap: "12px", minWidth: "220px" }}>
            <input
              autoFocus
              type="password"
              value={pinValue}
              onChange={e => setPinValue(e.target.value)}
              style={{ border: "1px solid #ccc", borderRadius: "4px", padding: "8px", fontSize: "16px", textAlign: "center" }}
            />
            <button type="submit" style={{ background: "#4a6fa5", color: "#fff", border: "none", borderRadius: "4px", padding: "8px", fontSize: "15px", cursor: "pointer" }}>אישור</button>
          </form>
        </div>
      )}
      {confirmDeleteIdx !== null && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.35)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", padding: "28px 32px", borderRadius: "10px", boxShadow: "0 4px 24px rgba(0,0,0,0.2)", display: "flex", flexDirection: "column", gap: "16px", minWidth: "260px", textAlign: "center", direction: "rtl" }}>
            <div style={{ fontSize: "17px", fontWeight: "bold" }}>לבטל את התזכורת?</div>
            <div style={{ fontSize: "14px", color: "#666" }}>הקרדיט יוחזר לבנק.</div>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
              <button disabled={isReadOnly} onClick={confirmDelete} style={{ background: "#dc3545", color: "#fff", border: "none", borderRadius: "6px", padding: "8px 24px", fontSize: "15px", cursor: "pointer" }}>בטל תזכורת</button>
              <button onClick={() => setConfirmDeleteIdx(null)} style={{ background: "#6c757d", color: "#fff", border: "none", borderRadius: "6px", padding: "8px 24px", fontSize: "15px", cursor: "pointer" }}>חזור</button>
            </div>
          </div>
        </div>
      )}
      <div class="modal fade" id="exampleModal" tabindex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <h1 class="modal-title col-12 fs-5" id="exampleModalLabel">
                <div className="col-12 d-flex flex-row justify-content-center align-items-center">
                  <div className={`fs-3 text-${modalTitle.mode}`}>{modalTitle.title}</div>
                  <img className="p-2" src={`./assets/icons/${modalTitle.icon}.svg`} height="35" width="35" />
                </div>
                <div className="col-12 d-flex flex-column justify-content-start align-items-center" style={{ maxHeight: "60vh", overflowY: "auto", padding: "10px", width: "100%" }}>
                  <div className="dashboard-list-row dashboard-list-row--header">
                    <div>#</div>
                    <div>שם</div>
                    <div>מוזמנים</div>
                    <div>טלפון</div>
                    <div>שם שולחן</div>
                    <div>מספר שולחן</div>
                  </div>
                  {modalTitle.list
                    .filter(el => el.name)
                    .map((el, index) => (
                      <div 
                        key={`${el.phoneNumber}-${index}`} 
                        className={`dashboard-list-row border-bottom border-${modalTitle.mode}`}
                      >
                        <div className="text-muted">
                          {index + 1}
                        </div>
                        <div>
                          {el.name}
                        </div>
                        <div>
                          {el.howMany}
                        </div>
                        <div>
                          {el.phoneNumber}
                        </div>
                        <div>{getContactTableDetails(el, seatingTables).name}</div>
                        <div>{getContactTableDetails(el, seatingTables).number}</div>
                      </div>
                    ))}
                </div>
              </h1>
            </div>
            <div className="modal-footer dashboard-modal-actions">
              <button type="button" disabled={Boolean(exportInProgress)} onClick={() => downloadExcel(modalTitle.list, modalTitle.title)} className="btn btn-success download-button download-button--excel">
                להורדת אקסל
              </button>
              <button type="button" disabled={Boolean(exportInProgress)} onClick={() => downloadPdf(modalTitle.list, modalTitle.title)} className="btn btn-outline-dark download-button download-button--pdf">
                להורדת PDF
              </button>
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                סגור
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="dashboard-header">
        {/* Decorative layers, shown by design-soft.css. */}
        <div
          className="dashboard-hero-art"
          aria-hidden="true"
          style={invitationImageUrl ? { "--hero-image": `url("${invitationImageUrl}")` } : undefined}
        />
        <h1>{ownerName}</h1>
        {(eventDateLabel || eventDetails.time || eventCountdown) && (
          <div className="dashboard-event-card">
            {(eventDateLabel || eventDetails.time) && (
              <div className="dashboard-header__event-meta">
                {eventDateLabel && (
                  <span>
                    <svg className="dashboard-event-card__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <rect x="3" y="5" width="18" height="16" rx="3" />
                      <path d="M7 3v4m10-4v4M3 11h18" />
                    </svg>
                    תאריך האירוע: <strong>{eventDateLabel}</strong>
                  </span>
                )}
                {eventDetails.time && (
                  <span>
                    <svg className="dashboard-event-card__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 7v5l3 2" />
                    </svg>
                    שעת האירוע: <strong>{eventDetails.time}</strong>
                  </span>
                )}
              </div>
            )}
            {eventCountdown && (
              <div className="dashboard-countdown" aria-live="polite">
                {eventCountdown.expired ? (
                  <strong>האירוע כבר התחיל</strong>
                ) : (
                  <>
                    <span>נשארו עוד</span>
                    <strong>
                      {eventCountdown.days} ימים · {eventCountdown.hours} שעות · {eventCountdown.minutes} דקות · {eventCountdown.seconds} שניות לאירוע
                    </strong>
                  </>
                )}
              </div>
            )}
            {eventCountdown && !eventCountdown.expired && (
              <div className="dashboard-countdown-chips" aria-hidden="true">
                <span><strong>{eventCountdown.days}</strong>ימים</span>
                <span><strong>{String(eventCountdown.hours).padStart(2, "0")}</strong>שעות</span>
                <span><strong>{String(eventCountdown.minutes).padStart(2, "0")}</strong>דקות</span>
                <span><strong>{String(eventCountdown.seconds).padStart(2, "0")}</strong>שניות</span>
              </div>
            )}
          </div>
        )}
      </div>

      {autopilotLifecycle?.status === "active" && <ActiveAutopilotBanner steps={autopilotPlan?.steps} />}

      {eventAdminData.autopilotAssignment && autopilotLifecycle?.status === "inactive" && (
        <div className="dashboard-autopilot-inactive" role="status">
          <strong>הטייס האוטומטי כבוי — לא יישלחו הודעות אוטומטיות לאורחים.</strong>
          <button type="button" disabled={contacts.length === 0 || recordUsage.overLimit} onClick={onOpenAutopilot}>הפעלת הטייס האוטומטי</button>
          {contacts.length === 0 && <small>יש להוסיף לפחות איש קשר אחד לפני ההפעלה.</small>}
        </div>
      )}

      {recordUsage.overLimit && (
        <div className="dashboard-autopilot-inactive" role="alert">
          <strong>חריגה ממכסת הרשומות</strong>
          <small>{recordOverageMessage(recordUsage)}</small>
        </div>
      )}

      {unreadSeatingNotifications.length > 0 && (
        <div className="dashboard-seating-alerts col-11">
          {unreadSeatingNotifications.map((notification) => (
            <div className="dashboard-seating-alert" key={notification.id}>
              <div className="dashboard-seating-alert__text">
                <strong>{notification.contactName}</strong>
                {" | "}
                <span>{notification.phoneNumber}</span>
                {" | "}
                <strong>{notification.howMany}</strong>
                {" מקומות - הוסר/ה מסידורי ההושבה. "}
                {notification.tableNumber ? (
                  <span>
                    בשולחן {notification.tableNumber}
                    {notification.tableName ? ` (${notification.tableName})` : ""} התפנו {notification.howMany} מקומות.
                  </span>
                ) : (
                  <span>התפנו {notification.howMany} מקומות בשולחן שהיה מוקצה לו/לה.</span>
                )}
              </div>
              <button
                type="button"
                className="btn btn-light btn-sm dashboard-seating-alert__button"
                disabled={isReadOnly}
                onClick={() => dismissSeatingNotification(notification.id)}
              >
                סמן כנקרא
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="dashboard-share-card">
        <div className="dashboard-share-card__section">
          <div className="dashboard-share-card__content">
            <strong className="dashboard-share-card__title">הזמנה + אישורי הגעה</strong>
            <span className="dashboard-share-card__full">לשלוח בעצמכם אישורי הגעה עם הזמנה וניווט</span>
            <span className="dashboard-share-card__short">לשלוח בעצמכם הזמנה + אישורי הגעה</span>
          </div>
          <div className="dashboard-share-card__actions">
            <InvitationLinkCopyButton
              addAlertText={addAlertText}
              docID={docID}
              version={invitationLinkVersion}
              className="dashboard-share-card__copy--rsvp"
              label={<ShareCopyLabel full="לינק להזמנה+אישורי הגעה" />}
            />
            <ShareLinkViews count={shareLinkViews.rsvp} />
          </div>
        </div>
        <div className="dashboard-share-card__section">
          <div className="dashboard-share-card__content">
            <strong className="dashboard-share-card__title">הזמנה בלבד</strong>
            <span className="dashboard-share-card__full" style={{ fontWeight: 400 }}>לשלוח בעצמכם <strong style={{ fontWeight: 700 }}>רק</strong> את ההזמנה וניווט לאירוע</span>
            <span className="dashboard-share-card__short" style={{ fontWeight: 400 }}>לשלוח בעצמכם <strong style={{ fontWeight: 700 }}>רק</strong> את ההזמנה</span>
          </div>
          <div className="dashboard-share-card__actions">
            <InvitationLinkCopyButton
              addAlertText={addAlertText}
              docID={docID}
              version={invitationLinkVersion}
              invitationOnly
              label={<ShareCopyLabel full="לינק להזמנה בלבד" />}
            />
            <ShareLinkViews count={shareLinkViews.invite} />
          </div>
        </div>
      </div>
      {/* <Donut/> */}
      <div
        // data-bs-toggle="modal"
        // data-bs-target="#exampleModal"
        // onClick={() => setModalTitle({ title: "סך הכל מוזמנים שאישרו הגעה", mode: "primary", icon: "check", list: approvedList })}
        className="dashboard-total-card"
      >
        <div className="dashboard-rsvp-ring" style={rsvpRingStyle} aria-hidden="true">
          <svg className="dashboard-rsvp-ring__svg" viewBox="0 0 120 120">
            <circle className="dashboard-rsvp-ring__track" cx="60" cy="60" r="50" pathLength="100" />
            {ringSegments.map((segment) => segment.length > 0 && (
              <circle
                key={segment.key}
                className={`dashboard-rsvp-ring__segment dashboard-rsvp-ring__segment--${segment.key}`}
                cx="60"
                cy="60"
                r="50"
                pathLength="100"
                strokeDasharray={`${segment.length} ${100 - segment.length}`}
                strokeDashoffset={segment.offset}
              />
            ))}
          </svg>
          <span><strong>{rsvpCounted ? Math.round((approved / rsvpCounted) * 100) : 0}%</strong>אישרו הגעה</span>
        </div>
        <div className="dashboard-total-card__summary">
          <span className="dashboard-total-card__label">סה"כ מוזמנים שיגיעו לאירוע</span>
          <strong className="dashboard-total-card__count">{total}</strong>
        </div>
        {freeVisibilityLimited && <small>סיכום אנשי הקשר המוצגים בלבד</small>}
        {freeRsvpVisibility.isFree && (
          <div className={`dashboard-free-notice${freeLimitReached ? " dashboard-free-notice--limit" : ""}`} role="status">
            {freeLimitReached ? (
              <>
                <strong className="dashboard-free-notice__title">הגעתם למגבלה של 50 אנשי קשר!</strong>
                <span>המערכת ממשיכה לצבור אישורים אבל הם לא מוצגים לכם בממשק</span>
              </>
            ) : <span>במסלול החינמי ניתן לצפות בעד 50 אנשי קשר.</span>}
            <button type="button" className="dashboard-free-notice__upgrade" onClick={onOpenAutopilot}>
              לפתיחת אישורי הגעה ללא הגבלה לחצו כאן
            </button>
          </div>
        )}
      </div>

      <div className="dashboard-status-grid">
        <div className="dashboard-status-card dashboard-status-card--danger">
          <button
            type="button"
            className="dashboard-status-card__summary"
            data-bs-toggle="modal"
            data-bs-target="#exampleModal"
            onClick={() => setModalTitle({ title: "אינם מגיעים", mode: "danger", icon: "out", list: notComingList })}
          >
            <img src="./assets/icons/out.svg" height="38" width="38" alt="" />
            <span className="dashboard-status-card__title">אנשי קשר שאינם מגיעים</span>
            <strong className="dashboard-status-card__count">{notComing}</strong>
          </button>
          <button
            type="button"
            className="dashboard-status-card__action-rail"
            data-bs-toggle="modal"
            data-bs-target="#exampleModal"
            aria-label="הצגת רשימת אינם מגיעים ואפשרויות הורדה"
            onClick={() => setModalTitle({ title: "אינם מגיעים", mode: "danger", icon: "out", list: notComingList })}
          >
            <StatusCardActionIcons />
          </button>
        </div>
        <div className="dashboard-status-card dashboard-status-card--success">
            <>
              <button
                type="button"
                className="dashboard-status-card__summary"
                data-bs-toggle="modal"
                data-bs-target="#exampleModal"
                onClick={() => setModalTitle({ title: "אישרו הגעה", mode: "success", icon: "check", list: approvedList })}
              >
                <img src="./assets/icons/check.svg" height="38" width="38" alt="" />
                <span className="dashboard-status-card__title">אנשי קשר שיגיעו לאירוע</span>
                <strong className="dashboard-status-card__count">{approved}</strong>
              </button>
              <button
                type="button"
                className="dashboard-status-card__action-rail"
                data-bs-toggle="modal"
                data-bs-target="#exampleModal"
                aria-label="הצגת רשימת אישרו הגעה ואפשרויות הורדה"
                onClick={() => setModalTitle({ title: "אישרו הגעה", mode: "success", icon: "check", list: approvedList })}
              >
                <StatusCardActionIcons />
              </button>
              {/* Compact free-plan note, shown by design-soft.css. */}
              {freeRsvpVisibility.isFree && !freeLimitReached && (
                <div className="dashboard-status-card__free">
                  <span>מסלול חינמי: צבירה עד 50 אנשי קשר</span>
                  <button type="button" onClick={onOpenAutopilot}>לשידרוג</button>
                </div>
              )}
            </>
        </div>

        <div className="dashboard-status-card dashboard-status-card--warning">
          <button
            type="button"
            className="dashboard-status-card__summary"
            data-bs-toggle="modal"
            data-bs-target="#exampleModal"
            onClick={() => setModalTitle({ title: "ממתינים לתשובה/לא יודעים", mode: "warning", icon: "pending", list: pendingList })}
          >
            <img src="./assets/icons/pending.svg" height="38" width="38" alt="" />
            <span className="dashboard-status-card__title">ממתינים לתשובה/לא יודעים</span>
            <strong className="dashboard-status-card__count">{pending}</strong>
          </button>
          <button
            type="button"
            className="dashboard-status-card__action-rail"
            data-bs-toggle="modal"
            data-bs-target="#exampleModal"
            aria-label="הצגת רשימת ממתינים לתשובה/לא יודעים ואפשרויות הורדה"
            onClick={() => setModalTitle({ title: "ממתינים לתשובה/לא יודעים", mode: "warning", icon: "pending", list: pendingList })}
          >
            <StatusCardActionIcons />
          </button>
        </div>
      </div>
      <RsvpEventSummary
        contacts={freeRsvpVisibility.visibleContacts}
        options={rsvpOptions}
        addAlertText={addAlertText}
        hideAssumptionNotes={isDemoDocument(docID)}
      />
      <MessageScheduleList
        messages={smsSchdule}
        hideContactCounts={freeVisibilityLimited}
        deliveryRecords={deliveryRecords}
        invitationImageUrl={invitationImageUrl}
        disabled={isReadOnly}
        onDelete={handleDeleteMessage}
        getMissingCount={getMissingCountForMessage}
        emptyText="אין עדיין סבבי הודעות שיצרתם."
      />
      {(showAnyBank || adminUnlocked) && (
        <div className="dashboard-banks">
          {(showSmsBank || adminUnlocked) && (
            <div className="dashboard-bank-card dashboard-bank-card--sms">
              <div className="dashboard-bank-card__heading">
                <MessageBankIcon channel="sms" />
                <span>בנק SMS</span>
              </div>
              <div className="dashboard-bank-card__balance">
                {adminUnlocked ? (
                  <input
                    className="dashboard-bank-card__input"
                    type="number"
                    value={smsBank}
                    onChange={e => setSmsBank(e.target.value)}
                  />
                ) : (
                  <strong>{smsBank}</strong>
                )}
                <small>הודעות נותרו</small>
              </div>
              {adminUnlocked && (
                <button disabled={isReadOnly} onClick={handleSaveSmsBank} className="btn btn-sm btn-outline-primary dashboard-bank-card__save">שמור SMS</button>
              )}
            </div>
          )}
          {(showWhatsAppBank || adminUnlocked) && (
            <div className="dashboard-bank-card dashboard-bank-card--whatsapp">
              <div className="dashboard-bank-card__heading">
                <MessageBankIcon channel="whatsapp" />
                <span>בנק וואטסאפ</span>
              </div>
              <div className="dashboard-bank-card__balance">
                {adminUnlocked ? (
                  <input
                    className="dashboard-bank-card__input"
                    type="number"
                    value={whatsAppBank}
                    onChange={e => setWhatsAppBank(e.target.value)}
                  />
                ) : (
                  <strong>{whatsAppBank}</strong>
                )}
                <small>הודעות נותרו</small>
              </div>
              {adminUnlocked && (
                <button disabled={isReadOnly} onClick={handleSaveWhatsAppBank} className="btn btn-sm btn-outline-success dashboard-bank-card__save">שמור וואטסאפ</button>
              )}
            </div>
          )}
        </div>
      )}
      {adminUnlocked && (
        <div className="dashboard-gift-reset" dir="rtl">
          <button type="button" disabled={isReadOnly} onClick={handleResetMessageGift}>
            איפוס מתנת ההודעות ללקוח
          </button>
          <small>המתנה תחזור רק כשגם בנק ה־SMS וגם בנק הוואטסאפ יהיו על 0.</small>
        </div>
      )}
      {watermarkShareNotice && <InvitationShareNotice docID={docID} invitationLinkVersion={invitationLinkVersion} addAlertText={addAlertText} setShowShareLinkModal={setWatermarkShareNotice} showShareExplanation={watermarkShareExplanation} setShowShareExplanation={setWatermarkShareExplanation} />}
      {adminUnlocked && (
        <section className="dashboard-autopilot-admin" dir="rtl">
          <strong>Watermark</strong>
          <button type="button" role="switch" aria-label="Watermark" aria-checked={Boolean(watermarkState?.enabled)} disabled={isReadOnly || watermarkBusy || !watermarkState} onClick={handleWatermarkToggle}>
            {watermarkBusy ? "טוען…" : watermarkState?.enabled ? "ON" : "OFF"}
          </button>
          {watermarkState?.cleanEntitled && <small>זכאות לתמונה נקייה קודמת להגדרה זו.</small>}
          {!watermarkState && !watermarkBusy && <small>בדיקת סימן המים אינה זמינה כרגע.</small>}
        </section>
      )}
      {adminUnlocked && (
        <section className="dashboard-autopilot-admin" dir="rtl">
          <div className="dashboard-autopilot-admin__heading">
            <div>
              <strong>Package / Autopilot Admin</strong>
              <small>CTRL-567 · נשמר באמצעות חשבון האירוע הקיים</small>
            </div>
            <span>{autopilotLifecycle?.status || "inactive"}</span>
          </div>
          {autopilotAdminBusy ? (
            <p>טוען הגדרות חבילה…</p>
          ) : (
            <>
              <div className="dashboard-autopilot-admin__assignment">
                <label>
                  Package Type
                  <select value={selectedPackageId} onChange={(event) => setSelectedPackageId(event.target.value)}>
                    <option value="">בחר חבילה</option>
                    <option value="__free__">FREE · גישה מפורשת</option>
                    <option value="__manual__">MANUAL · גישה ידנית מלאה</option>
                    {autopilotTemplates.map((item) => <option key={item.id} value={item.id}>{item.name} · revision {item.revision}</option>)}
                    <option value="__remove__">הסר חבילה</option>
                  </select>
                </label>
                <label>
                  Record Limit
                  <input type="number" min="1" step="1" disabled={["__remove__", "__free__", "__manual__"].includes(selectedPackageId)} value={recordLimitInput} onChange={(event) => setRecordLimitInput(event.target.value)} />
                </label>
                <button type="button" disabled={isReadOnly || !selectedPackageId || autopilotAdminBusy} onClick={handleSavePackageAssignment}>
                  {selectedPackageId === "__remove__" ? "הסר חבילה" : ["__free__", "__manual__"].includes(selectedPackageId) ? "שמור מצב גישה" : "שמור חבילה ומכסה"}
                </button>
              </div>
              <p className="dashboard-autopilot-admin__note">
                שמירת חבילה אינה מפעילה את הטייס האוטומטי. חריגה מהמכסה אינה מוחקת אנשי קשר, אך חוסמת הפעלה וביצוע של שלבים עתידיים עד להסדרתה.
              </p>
              <div className="dashboard-autopilot-admin__reset">
                <button
                  type="button"
                  disabled={isReadOnly || autopilotAdminBusy || (!autopilotPlan && autopilotLifecycle?.status === "inactive")}
                  onClick={() => setShowAutopilotResetConfirm(true)}
                >
                  איפוס הטייס האוטומטי
                </button>
                <small>מאפס רק את ההפעלה וציר הזמן הנוכחי. אנשי קשר, חבילה, פתיח קולי והיסטוריית שליחות נשמרים.</small>
              </div>
              {showAutopilotResetConfirm && (
                <div className="dashboard-autopilot-reset-dialog" role="dialog" aria-modal="true" aria-labelledby="autopilot-reset-title">
                  <div className="dashboard-autopilot-reset-dialog__panel">
                    <strong id="autopilot-reset-title">איפוס הטייס האוטומטי</strong>
                    <p>
                      איפוס הטייס האוטומטי יחזיר אותו למצב לא פעיל וימחק את ציר הזמן הנוכחי. היסטוריית ההודעות והחיוגים שכבר בוצעו תישמר. הפעלה מחדש תיצור ציר זמן חדש ועלולה לתזמן ולשלוח מחדש שלבים מהחבילה. האם להמשיך?
                    </p>
                    <div className="dashboard-autopilot-reset-dialog__actions">
                      <button type="button" disabled={autopilotAdminBusy} onClick={handleResetAutopilot}>מאשר לאפס</button>
                      <button type="button" disabled={autopilotAdminBusy} onClick={() => setShowAutopilotResetConfirm(false)}>ביטול</button>
                    </div>
                  </div>
                </div>
              )}
              {autopilotPlan && (
                <div className="dashboard-autopilot-admin__plan">
                  <div className="dashboard-autopilot-admin__plan-title">
                    <strong>Event Autopilot Plan · revision {autopilotLifecycle?.planRevision || autopilotPlan.planRevision}</strong>
                    <button type="button" onClick={handleAddAutopilotStep}>הוסף שלב עתידי</button>
                  </div>
                  {autopilotDraftSteps.map((step) => {
                    const completed = step.executionStatus === "completed" || step.status === "completed";
                    return (
                      <div key={step.id} className={`dashboard-autopilot-admin__step ${completed ? "is-completed" : ""}`}>
                        <code>{step.id}</code>
                        <label>
                          <span>{step.kind} · סוג הודעה</span>
                          <select
                            aria-label={`Message Type · ${step.id}`}
                            disabled={completed || step.kind !== "message" || isReadOnly}
                            value={step.kind === "message" ? (step.messagePurpose || "") : ""}
                            onChange={(event) => updateAutopilotDraftStep(step.id, { messagePurpose: event.target.value })}
                          >
                            {step.kind === "message" ? (
                              <>
                                {!AUTOPILOT_MESSAGE_PURPOSES.includes(step.messagePurpose) && (
                                  <option value={step.messagePurpose || ""} disabled>{step.messagePurpose || "בחר סוג הודעה"}</option>
                                )}
                                {AUTOPILOT_MESSAGE_PURPOSES.map((purpose) => (
                                  <option key={purpose} value={purpose}>{purpose}</option>
                                ))}
                              </>
                            ) : <option value="">לא רלוונטי</option>}
                          </select>
                        </label>
                        <input
                          type="datetime-local"
                          disabled={completed}
                          value={formatJerusalemDateTimeLocal(step.scheduledAt)}
                          onChange={(event) => {
                            try {
                              updateAutopilotDraftStep(step.id, { scheduledAt: parseJerusalemDateTimeLocal(event.target.value) });
                            } catch (_error) {
                              updateAutopilotDraftStep(step.id, { scheduledAt: "" });
                            }
                          }}
                        />
                        <select
                          disabled={completed || step.kind === "voice" || step.kind === "thankYou"}
                          value={step.channel}
                          onChange={(event) => updateAutopilotDraftStep(step.id, { channel: event.target.value })}
                        >
                          <option value="whatsapp">WhatsApp</option>
                          <option value="sms">SMS</option>
                          {step.kind === "voice" && <option value="voice">Voice</option>}
                        </select>
                        {!completed && <button type="button" onClick={() => setAutopilotDraftSteps((steps) => steps.filter((item) => item.id !== step.id))}>מחק</button>}
                      </div>
                    );
                  })}
                  <button type="button" disabled={isReadOnly} onClick={handleSaveFuturePlan}>שמור שינויים בשלבים עתידיים</button>
                </div>
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
};

export default Dashboard;
